import { createSlice } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'
import { ContactSettings } from 'src/types/contact'
import { HTTP } from '../lib/axios'
import type { AppThunk } from '../store'

interface AccountState {
  loading: boolean
  saving: boolean
  accounts: Array<any>
  users: Array<any>
  accountJwt: string
  contactSettings: {
    [key: string]: ContactSettings
  }
  clio: {
    data: any
    loading: boolean
  }
  nylas: {
    data: any
    loading: boolean
  }
}

const initialState: AccountState = {
  loading: false,
  saving: false,
  accounts: [],
  users: [],
  accountJwt: '',
  contactSettings: {},
  clio: {
    data: null,
    loading: true
  },
  nylas: {
    data: null,
    loading: true
  }
}

const slice = createSlice({
  name: 'account',
  initialState,
  reducers: {
    setAccounts(state: AccountState, { payload }: PayloadAction<any>): void {
      state.accounts = payload
    },
    setContactSettings(
      state: AccountState,
      { payload }: PayloadAction<any>
    ): void {
      state.contactSettings = payload
    },
    setClio(state: AccountState, { payload }: PayloadAction<any>): void {
      state.clio = payload
    },
    setNylas(state: AccountState, { payload }: PayloadAction<any>): void {
      state.nylas = payload
    },
    setUsers(state: AccountState, { payload }: PayloadAction<any>): void {
      state.users = payload
    },
    setAccountJwt(state: AccountState, { payload }: PayloadAction<any>): void {
      state.accountJwt = payload
    },
    setLoading(state: AccountState, { payload }: PayloadAction<boolean>): void {
      state.loading = payload
    },
    setSaving(state: AccountState, { payload }: PayloadAction<boolean>): void {
      state.saving = payload
    }
  }
})

export const { reducer } = slice

export const {
  setAccounts,
  setContactSettings,
  setClio,
  setNylas,
  setUsers,
  setLoading,
  setSaving,
  setAccountJwt
} = slice.actions

export const getAccounts =
  (token: string): AppThunk =>
  async (dispatch): Promise<void> => {
    dispatch(setLoading(true))
    try {
      const res = await HTTP.get('/users/accounts', {
        headers: {
          Authorization: `Bearer ${token}`
        }
      })
      const { data } = res.data
      dispatch(setAccounts(data.Items))
      dispatch(setLoading(false))
    } catch (err) {
      console.error(err)
      dispatch(setLoading(false))
    }
  }

export const getAccountJwt =
  (token: string, accountId: string): AppThunk =>
  async (dispatch): Promise<void> => {
    dispatch(setLoading(true))
    try {
      const res = await HTTP.get(`/users/${accountId}/jwt`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      })
      const { data } = res.data
      dispatch(setAccountJwt(data.token))
      dispatch(setLoading(false))
    } catch (err) {
      console.error(err)
      dispatch(setLoading(false))
    }
  }

export const getUsers =
  (token: string, accountId: string): AppThunk =>
  async (dispatch): Promise<void> => {
    dispatch(setLoading(true))
    try {
      const res = await HTTP.get(`/users/${accountId}/users`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      })

      const { data } = res.data
      dispatch(setUsers(data))
      dispatch(setLoading(false))
    } catch (err) {
      console.error(err)
      dispatch(setLoading(false))
    }
  }

export const getClio =
  (token: string, accountId: string, australisId: string): AppThunk =>
  async (dispatch, getState) => {
    const { clio } = getState().account
    dispatch(setClio({ ...clio, loading: true }))

    try {
      const result = await HTTP.get(`/clio/${accountId}/user/${australisId}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      })
      dispatch(setClio({ data: result.data.message, loading: false }))
    } catch (err) {
      console.log(err)
      dispatch(setClio({ ...clio, loading: false }))
    }
  }

export const getNylas =
  (token: string, accountId: string, australisId: string): AppThunk =>
  async (dispatch, getState) => {
    const { nylas } = getState().account
    dispatch(setNylas({ ...nylas, loading: true }))

    try {
      const result = await HTTP.get('/nylas/account', {
        headers: {
          Authorization: `Bearer ${token}`
        }
      })
      dispatch(setNylas({ data: result.data, loading: false }))
    } catch (err) {
      console.log(err)
      dispatch(setNylas({ ...nylas, loading: false }))
    }
  }

export const saveContactSettings = (
  token: string,
  accountId: string,
  settings: any
): AppThunk => {
  return async (dispatch) => {
    dispatch(setSaving(true))

    try {
      const res = await HTTP.post(
        `/contacts/account/${accountId}/settings`,
        { contactSettingsTemplates: settings },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      )

      const { contactSettingsTemplates } = res.data.account.contactsData
      dispatch(setContactSettings({ ...contactSettingsTemplates }))
    } catch (error) {
      console.error(error)
    } finally {
      dispatch(setSaving(false))
    }
  }
}
